"use client";

import { useEffect, useState } from "react";
import { Loader2, Download, Copy } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  ReviewQuestionsClient,
  type ReviewQuestionRow,
} from "@/components/admin/review-questions/review-questions-client";
import { ImportReviewQuestionsDialog } from "@/components/show-details/ImportReviewQuestionsDialog";
import { listReviewQuestions } from "@/lib/reviews/admin-actions";

/**
 * "Review questions" tab on the show settings page.
 *
 * Surfaces the DB-driven QR review/feedback question editor at the SHOW level
 * (per the 2026-06-12 product decision that questions are configured per show,
 * not per venue).
 *
 * NOTE: this pass is UI only. The editor is currently backed by the existing
 * venue-level store (`review_questions.venue_id`); per-show storage
 * (`review_questions.show_id` + show-scoped resolution) is a deferred follow-up.
 */
export function ShowReviewQuestionsTab({
  venueId,
  showId,
  languages,
}: {
  venueId: string;
  showId: number;
  showName?: string;
  /** Languages configured for this venue (codes, e.g. ["en", "sv"]). */
  languages?: string[];
}) {
  const [questions, setQuestions] = useState<ReviewQuestionRow[] | null>(null);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [isImportOpen, setIsImportOpen] = useState(false);

  useEffect(() => {
    let active = true;
    listReviewQuestions(showId).then((rows) => {
      if (active) setQuestions(rows as ReviewQuestionRow[]);
    });
    return () => {
      active = false;
    };
  }, [showId, refreshIndex]);

  // Mirror the /qr page: use the configured app URL (localhost in dev) and fall
  // back to the production host so the link is always valid.
  const appBase = (
    process.env.NEXT_PUBLIC_APP_URL || "https://app.onairgameshows.com"
  ).replace(/\/+$/, "");
  const qrUrl = `${appBase}/qr/${venueId}/${showId}`;

  return (
    <div className="space-y-4">
      {/* Show QR Code link (read-only, for staff reference) */}
      <div className="border rounded-lg p-6 space-y-2">
        <Label className="text-sm font-medium text-gray-700">
          Show QR Code link
        </Label>
        <p className="text-xs text-gray-500">
          The link this show&apos;s review QR code points to. Share or print it
          so customers can scan and leave a review.
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-800 break-all">
            {qrUrl}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              navigator.clipboard
                .writeText(qrUrl)
                .then(() => toast.success("Link copied"))
                .catch(() => toast.error("Failed to copy link"));
            }}
            className="flex items-center gap-2 shrink-0"
          >
            <Copy className="w-4 h-4" />
            Copy
          </Button>
        </div>
      </div>

      {questions === null ? (
        <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading questions…
        </div>
      ) : (
        <ReviewQuestionsClient
          venueId={venueId}
          showId={showId}
          questions={questions}
          languages={languages}
          hasGlobalDefaults={false}
          headerActions={
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsImportOpen(true)}
            >
              <Download className="mr-2 h-4 w-4" />
              Copy from other show
            </Button>
          }
        />
      )}

      <ImportReviewQuestionsDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        venueId={Number(venueId)}
        showId={showId}
        onImported={() => setRefreshIndex((i) => i + 1)}
      />
    </div>
  );
}
